import React, { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import type { CommerceSelectOptionViewModel } from "../../contracts/commerce-admin";
import { cn } from "../../lib/utils";
import { DescriptionText, HorizontalStack, Surface, VerticalStack } from "../blocks";
import { Popover, PopoverContent, PopoverTrigger } from "../../internal/primitives/popover";

type CommerceSelectorBaseProps = {
  options: CommerceSelectOptionViewModel[];
  placeholder: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
};

type CommerceObjectSelectorProps = CommerceSelectorBaseProps & {
  value?: string | null;
  onChange: (value: string) => void;
  allowClear?: boolean;
};

type CommerceObjectMultiSelectorProps = CommerceSelectorBaseProps & {
  values: string[];
  onChange: (values: string[]) => void;
  noneSelectedLabel?: string;
};

function normalizeText(value: string): string {
  return value.trim().toLowerCase();
}

function optionMatchesQuery(option: CommerceSelectOptionViewModel, query: string): boolean {
  if (!query) {
    return true;
  }
  const haystack = [
    option.label,
    option.description,
    option.helperText,
    option.badge,
    option.groupLabel,
    ...(option.keywords || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function filterOptions(options: CommerceSelectOptionViewModel[], query: string) {
  const normalizedQuery = normalizeText(query);
  return options.filter(option => optionMatchesQuery(option, normalizedQuery));
}

function groupOptions(options: CommerceSelectOptionViewModel[]) {
  const groups = new Map<string, CommerceSelectOptionViewModel[]>();
  options.forEach(option => {
    const groupLabel = option.groupLabel?.trim() || "";
    const existing = groups.get(groupLabel);
    if (existing) {
      existing.push(option);
      return;
    }
    groups.set(groupLabel, [option]);
  });
  return Array.from(groups.entries());
}

function SelectedValueSummary({
  option,
  placeholder,
  loading,
}: {
  option?: CommerceSelectOptionViewModel;
  placeholder: string;
  loading?: boolean;
}) {
  if (loading) {
    return <span className="truncate text-sm font-medium text-muted-foreground">Loading options...</span>;
  }
  if (!option) {
    return <span className="truncate text-sm font-medium text-muted-foreground">{placeholder}</span>;
  }

  return (
    <VerticalStack gap={0} className="min-w-0">
      <span className="truncate text-sm font-bold text-foreground">{option.label}</span>
      {option.description ? (
        <span className="truncate text-[11px] text-muted-foreground">{option.description}</span>
      ) : null}
    </VerticalStack>
  );
}

function SelectorOptionRow({
  option,
  selected,
  onSelect,
  multi,
}: {
  option: CommerceSelectOptionViewModel;
  selected: boolean;
  onSelect: () => void;
  multi?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={option.disabled}
      className={cn(
        "flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors",
        selected ? "bg-primary/8 text-foreground" : "hover:bg-muted/30 text-foreground",
        option.disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
          selected ? "border-primary/50 bg-primary/10 text-primary" : "border-border/60 bg-background text-transparent",
        )}
      >
        <Check className="h-3 w-3" />
      </span>
      <VerticalStack gap={1} className="min-w-0 flex-1">
        <HorizontalStack gap={2} className="min-w-0 items-center">
          <span className="truncate text-sm font-bold text-foreground">{option.label}</span>
          {option.badge ? (
            <span className="shrink-0 rounded border border-border/50 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground">
              {option.badge}
            </span>
          ) : null}
          {multi && selected ? (
            <span className="shrink-0 text-[10px] font-black uppercase tracking-[0.18em] text-primary">Selected</span>
          ) : null}
        </HorizontalStack>
        {option.description ? (
          <span className="truncate text-[11px] text-muted-foreground">{option.description}</span>
        ) : null}
        {option.helperText ? (
          <span className="truncate text-[10px] uppercase tracking-[0.14em] text-muted-foreground/70">
            {option.helperText}
          </span>
        ) : null}
      </VerticalStack>
    </button>
  );
}

function SelectorSearchPopover({
  open,
  onOpenChange,
  trigger,
  options,
  selectedValues,
  onSelect,
  searchPlaceholder,
  emptyMessage,
  multi,
}: {
  open: boolean;
  onOpenChange: (nextOpen: boolean) => void;
  trigger: React.ReactNode;
  options: CommerceSelectOptionViewModel[];
  selectedValues: Set<string>;
  onSelect: (value: string) => void;
  searchPlaceholder: string;
  emptyMessage: string;
  multi?: boolean;
}) {
  const [query, setQuery] = useState("");

  const filteredOptions = useMemo(() => filterOptions(options, query), [options, query]);
  const groupedOptions = useMemo(() => groupOptions(filteredOptions), [filteredOptions]);

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          setQuery("");
        }
        onOpenChange(nextOpen);
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-[380px] border-border bg-background p-3 shadow-xl">
        <VerticalStack gap={3}>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/60" />
            <input
              autoFocus
              className="h-9 w-full rounded-md border border-border/60 bg-background pl-9 pr-3 text-sm text-foreground outline-none transition-colors focus:border-primary/40"
              placeholder={searchPlaceholder}
              onChange={event => setQuery(event.target.value)}
              value={query}
              aria-label={searchPlaceholder}
            />
          </div>
          {groupedOptions.length === 0 ? (
            <Surface className="border-dashed border-border/60 bg-muted/10 p-4 text-center">
              <DescriptionText className="text-xs">{emptyMessage}</DescriptionText>
            </Surface>
          ) : (
            <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
              {groupedOptions.map(([groupLabel, group]) => (
                <VerticalStack key={groupLabel || "default"} gap={1}>
                  {groupLabel ? (
                    <p className="px-1 text-[10px] font-black uppercase tracking-[0.18em] text-muted-foreground/70">
                      {groupLabel}
                    </p>
                  ) : null}
                  <Surface className="border-border/50 p-1">
                    <VerticalStack gap={0}>
                      {group.map(option => (
                        <SelectorOptionRow
                          key={option.value}
                          option={option}
                          multi={multi}
                          selected={selectedValues.has(option.value)}
                          onSelect={() => onSelect(option.value)}
                        />
                      ))}
                    </VerticalStack>
                  </Surface>
                </VerticalStack>
              ))}
            </div>
          )}
        </VerticalStack>
      </PopoverContent>
    </Popover>
  );
}

export function CommerceObjectSelector({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder = "Search options...",
  emptyMessage = "No matching options.",
  disabled = false,
  loading = false,
  className,
  allowClear = true,
}: CommerceObjectSelectorProps) {
  const [open, setOpen] = useState(false);
  const selectedOption = useMemo(
    () => options.find(option => option.value === (value || "")),
    [options, value],
  );

  return (
    <div className={className}>
      <SelectorSearchPopover
        open={open}
        onOpenChange={nextOpen => {
          if (disabled || loading) {
            return;
          }
          setOpen(nextOpen);
        }}
        options={options}
        selectedValues={new Set(value ? [value] : [])}
        onSelect={nextValue => {
          onChange(nextValue);
          setOpen(false);
        }}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        trigger={
          <button
            type="button"
            disabled={disabled || loading}
            className={cn(
              "flex min-h-[44px] w-full items-center justify-between gap-3 rounded-md border border-border/60 bg-background px-3 py-2 text-left transition-colors hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-60",
            )}
          >
            <SelectedValueSummary option={selectedOption} placeholder={placeholder} loading={loading} />
            <HorizontalStack gap={1} className="shrink-0 items-center">
              {allowClear && value ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={event => {
                    event.stopPropagation();
                    onChange("");
                    setOpen(false);
                  }}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      onChange("");
                      setOpen(false);
                    }
                  }}
                  className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Clear selection"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              ) : null}
              <ChevronsUpDown className="h-4 w-4 text-muted-foreground/70" />
            </HorizontalStack>
          </button>
        }
      />
    </div>
  );
}

export function CommerceObjectMultiSelector({
  options,
  values,
  onChange,
  placeholder,
  searchPlaceholder = "Search options...",
  emptyMessage = "No matching options.",
  disabled = false,
  loading = false,
  className,
  noneSelectedLabel = "No selections yet.",
}: CommerceObjectMultiSelectorProps) {
  const [open, setOpen] = useState(false);
  const selectedValues = useMemo(() => new Set(values), [values]);
  const selectedOptions = useMemo(
    () => options.filter(option => selectedValues.has(option.value)),
    [options, selectedValues],
  );

  return (
    <VerticalStack gap={2} className={className}>
      <SelectorSearchPopover
        open={open}
        onOpenChange={nextOpen => {
          if (disabled || loading) {
            return;
          }
          setOpen(nextOpen);
        }}
        options={options}
        selectedValues={selectedValues}
        onSelect={nextValue => {
          if (selectedValues.has(nextValue)) {
            onChange(values.filter(value => value !== nextValue));
            return;
          }
          onChange([...values, nextValue]);
        }}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        multi
        trigger={
          <button
            type="button"
            disabled={disabled || loading}
            className="flex min-h-[44px] w-full items-center justify-between gap-3 rounded-md border border-border/60 bg-background px-3 py-2 text-left transition-colors hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <VerticalStack gap={0} className="min-w-0">
              <span className="truncate text-sm font-bold text-foreground">
                {loading
                  ? "Loading options..."
                  : selectedOptions.length > 0
                    ? `${selectedOptions.length} selected`
                    : placeholder}
              </span>
              <span className="truncate text-[11px] text-muted-foreground">
                {selectedOptions.length > 0
                  ? selectedOptions.map(option => option.label).join(", ")
                  : noneSelectedLabel}
              </span>
            </VerticalStack>
            <HorizontalStack gap={1} className="shrink-0 items-center">
              {values.length > 0 ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={event => {
                    event.stopPropagation();
                    onChange([]);
                    setOpen(false);
                  }}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      event.stopPropagation();
                      onChange([]);
                      setOpen(false);
                    }
                  }}
                  className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Clear selections"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              ) : null}
              <ChevronsUpDown className="h-4 w-4 text-muted-foreground/70" />
            </HorizontalStack>
          </button>
        }
      />

      <div className="flex min-h-[28px] flex-wrap gap-1.5">
        {selectedOptions.length === 0 ? (
          <span className="text-xs text-muted-foreground/70">{noneSelectedLabel}</span>
        ) : (
          selectedOptions.map(option => (
            <span
              key={option.value}
              className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-muted/30 px-2 py-1 text-xs font-semibold text-foreground"
            >
              <span className="max-w-[220px] truncate">{option.label}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(values.filter(value => value !== option.value))}
                className="rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed"
                aria-label={`Remove ${option.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))
        )}
      </div>
    </VerticalStack>
  );
}
